import type { Reference, ServiceRequest } from '@medplum/fhirtypes'
import { formatDateTime } from '@seen/core/dates'
import * as appointmentsLib from '@seen/core/models/appointments/index'
import {
  OrderCategory,
  orderCategoryLabel,
} from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import { IconCalendarEvent } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { OrderOptionDisplay } from '#src/components/OrderOptionDisplay.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { AppointmentLink } from '#src/pages/participants/timeline/AppointmentLink.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'

export function OrderOption({
  order: orderReference,
  isSelected,
}: {
  order: Reference<ServiceRequest>
  isSelected?: boolean
}): JSX.Element | null {
  const { data: order, isLoading } = useRead(Order, orderReference)

  const title = getOrderTitle(order)
  const categoryLabel =
    !!order?.category && order.category !== OrderCategory.Misc
      ? orderCategoryLabel(order.category)
      : ''

  if (isLoading) {
    return <Loader className="size-4" />
  }
  if (!order) {
    return null
  }

  return (
    <OrderOptionDisplay
      seenIdentifier={order.seenIdentifier ?? ''}
      categoryLabel={categoryLabel}
      title={title}
      status={order.status}
      requester={order.requester}
      performer={order.performer}
      signedBy={order.signedBy}
      signedDate={order.signedDate}
      isSelected={isSelected}
    >
      <OrderAppointmentsIcon order={order} />
    </OrderOptionDisplay>
  )
}

function OrderAppointmentsIcon({
  order,
}: {
  order: Order
}): JSX.Element | null {
  const { data: appointments = [] } = useSearch(
    appointmentsLib.ExtendedAppointment,
    {
      ...appointmentsLib.ExtendedAppointment.byBasedOn(order?.reference ?? ''),
      status: 'booked',
    },
    { query: { enabled: !!order?.reference } }
  )

  if (appointments.length === 0) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger>
        <IconCalendarEvent className="size-3.5 text-muted-foreground" />
      </TooltipTrigger>
      <TooltipContent>
        {appointments.map((appointment) => (
          <AppointmentLink key={appointment.id} appointment={appointment}>
            {appointment.title}
            {appointment.start && (
              <>
                {' @ '}
                {formatDateTime(appointment.start)}
              </>
            )}
          </AppointmentLink>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}
